import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { useMpRoom } from "../multiplayer/useMpRoom";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { Fireworks } from "../components/Fireworks";
import { BattlePodiumScene } from "../components/BattlePodiumScene";
import type { MpPlayer } from "../multiplayer/types";
import { clearSession } from "../local/session";
import { startPodiumMusic, fadeOutPodiumMusic, startFinalWinMusic, stopFinalWinMusic } from "../game/audio";
import { AvatarPreview } from "../components/AvatarEditor";
import { getTeachingLevel } from "../data/teaching-levels";
import { levelThumbnail } from "../components/levelThumbnails";
import { isFinaleLesson } from "../local/lessonId";
import { RaceLobby, type RaceEmbedProps } from "./RaceLobby";
import { RacePlay } from "./RacePlay";

/** Pull the trailing level number off a lessonId (both `pack:x::L3` and
 *  `people-L3` shapes). */
function levelIndexOf(lessonId: string): number {
  const m = /::L(\d+)$/.exec(lessonId) ?? /[-_]L(\d+)$/i.exec(lessonId);
  return m ? Number(m[1]) : 1;
}

/** One candidate-level card in the post-round vote. The 3D course snapshot is
 *  rendered off the main thread (deferred a frame) so the screen doesn't jank
 *  when the results page mounts. */
function VoteLevelCard({
  lessonId, count, total, mine, onVote,
}: {
  lessonId: string;
  count: number;
  total: number;
  mine: boolean;
  onVote: () => void;
}) {
  const tl = getTeachingLevel(levelIndexOf(lessonId));
  const [thumb, setThumb] = useState("");
  useEffect(() => {
    const raf = requestAnimationFrame(() => setThumb(tl ? levelThumbnail(tl.courseMode) : ""));
    return () => cancelAnimationFrame(raf);
  }, [tl?.courseMode]);
  return (
    <button className={`vote-card ${mine ? "is-mine" : ""}`} onClick={onVote}>
      {mine && <span className="vote-card-check" aria-hidden>✓</span>}
      <span className="vote-card-thumb">
        {thumb
          ? <img src={thumb} alt="" />
          : <span className="vote-card-thumb-ph">{tl?.title?.[0] ?? "?"}</span>}
      </span>
      <span className="vote-card-name">{tl?.title ?? `Level ${levelIndexOf(lessonId)}`}</span>
      <span className="vote-card-tally">
        <span className="vote-card-bar"><i style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }} /></span>
        <span className="vote-card-count">{count} {count === 1 ? "vote" : "votes"}</span>
      </span>
    </button>
  );
}

/**
 * Composite ranking: accuracy (correct/total) → score → time.
 * DNFs sink to the bottom by score.
 */
function rankPlayers(players: MpPlayer[]): MpPlayer[] {
  return [...players].sort((a, b) => {
    const aDone = a.finished;
    const bDone = b.finished;
    if (aDone !== bDone) return aDone ? -1 : 1;
    if (!aDone) return (b.finishScore ?? 0) - (a.finishScore ?? 0);
    const aAcc = (a.finishCorrect ?? 0) / Math.max(1, a.finishTotal ?? 1);
    const bAcc = (b.finishCorrect ?? 0) / Math.max(1, b.finishTotal ?? 1);
    if (aAcc !== bAcc) return bAcc - aAcc;
    if ((b.finishScore ?? 0) !== (a.finishScore ?? 0))
      return (b.finishScore ?? 0) - (a.finishScore ?? 0);
    return (a.finishTimeMs ?? 1e9) - (b.finishTimeMs ?? 1e9);
  });
}

/**
 * Survival (Battle Royale) ranking: there's no finish line — the goal is to
 * fall LAST. So a player still standing (not finished) outranks anyone who
 * fell, and among those who fell, a LATER fall (`finishTimeMs` = survival
 * duration) beats an earlier one. "Die first loses" falls straight out of
 * ranking by time descending.
 */
function rankPlayersSurvival(players: MpPlayer[]): MpPlayer[] {
  return [...players].sort((a, b) => {
    const aFell = a.finished;
    const bFell = b.finished;
    if (aFell !== bFell) return aFell ? 1 : -1; // still-standing first
    if (!aFell) return (b.finishScore ?? 0) - (a.finishScore ?? 0); // both up → by score
    const at = a.finishTimeMs ?? 0;
    const bt = b.finishTimeMs ?? 0;
    if (at !== bt) return bt - at; // both fell → later fall wins
    return (b.finishScore ?? 0) - (a.finishScore ?? 0);
  });
}

function placeOrdinal(place: number): string {
  if (place === 1) return "1st";
  if (place === 2) return "2nd";
  if (place === 3) return "3rd";
  return `${place}th`;
}

export function RaceResults({ codeOverride, identity, embedded }: RaceEmbedProps = {}) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { profile } = useLocalProfile();
  const code = codeOverride ?? params.get("code");

  if (!embedded && !profile) {
    nav("/", { replace: true });
    return null;
  }
  const me = identity ?? {
    id: getBattlePlayerId(profile!.id, code),
    displayName: profile!.displayName,
    avatarConfig: profile!.avatarConfig,
  };

  const joinOpts = useMemo(
    () => (code ? { code, asHost: false, me } : null),
    [code, me.id],
  );
  const { client, state } = useMpRoom(joinOpts);

  // Follow the room phase: Back-to-Lobby → /battle, Play-Again /
  // Next-Battle → /battle/play (server moves the phase directly to
  // countdown without a lobby trip). Embedded skips the navigation — the
  // render body below swaps in RaceLobby/RacePlay directly instead.
  useEffect(() => {
    if (!state || !code || embedded) return;
    if (state.phase === "lobby") {
      const hostQuery = state.hostId === me.id ? "&host=1" : "";
      nav(`/battle?code=${code}${hostQuery}`, { replace: true });
    } else if (state.phase === "countdown" || state.phase === "racing") {
      nav(`/battle/play?code=${code}`, { replace: true });
    }
  }, [state?.phase, state?.hostId, code, me.id, nav, embedded]);

  // Podium music. The final-level (Battle Royale) podium plays the dedicated
  // win track regardless of the music setting; every other podium plays the
  // theme only if the player has music turned on. Fades out on leave.
  const podiumLessonId = state?.lessonId;
  useEffect(() => {
    if (podiumLessonId === undefined) return;
    if (isFinaleLesson(podiumLessonId)) {
      startFinalWinMusic();
      return () => stopFinalWinMusic();
    }
    startPodiumMusic();
    return () => fadeOutPodiumMusic(1800);
  }, [podiumLessonId]);

  // Auto-return-to-lobby timer removed — host now chooses Back to
  // Lobby / Play Again / Next Battle explicitly; nothing fires on a
  // timeout.

  // Staged reveal of UI sections so the screen "comes alive" instead of
  // dumping everything on screen at once.
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (state?.phase !== "results") return;
    const t = [
      window.setTimeout(() => setStep(1), 350),
      window.setTimeout(() => setStep(2), 800),
      window.setTimeout(() => setStep(3), 1300),
    ];
    return () => t.forEach(clearTimeout);
  }, [state?.phase]);

  // Re-render twice a second while the vote timer is live so the countdown
  // pill ticks down. The server is authoritative on when it actually resolves.
  const voteDeadline = state?.voteDeadline;
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!voteDeadline) return;
    const id = window.setInterval(() => forceTick((n) => (n + 1) & 0xffff), 500);
    return () => window.clearInterval(id);
  }, [voteDeadline]);

  if (!state) return <div className="full-center">Loading…</div>;
  if (embedded && state.phase === "lobby") {
    return <RaceLobby codeOverride={code!} identity={identity} embedded asHost={state.hostId === me.id} />;
  }
  if (embedded && (state.phase === "countdown" || state.phase === "racing")) {
    return <RacePlay codeOverride={code!} identity={identity} embedded />;
  }
  const finaleLevel = isFinaleLesson(state.lessonId);
  const ranked = finaleLevel ? rankPlayersSurvival(state.players) : rankPlayers(state.players);
  const myRank = ranked.findIndex((p) => p.id === me.id) + 1;
  const winner = ranked[0];
  const youWon = winner?.id === me.id;
  const isHost = state.hostId === me.id;

  // Background 3D character: the player viewing the screen — their own
  // avatar in a hero pose. Wins fire the flawless animation; otherwise it's
  // the standard idle.
  const heroAvatar = me.avatarConfig;

  const topThree = ranked.slice(0, 3);
  const restRanked = ranked.slice(3);
  const computedScore = (p: MpPlayer) => p.finishScore ?? ((p.finishCorrect ?? 0) * 100);

  void heroAvatar; void myRank;

  // Post-round level vote.
  const votes = state.votes ?? {};
  const myVote = votes[me.id];
  const voteCount = (opt: string) => Object.values(votes).filter((v) => v === opt).length;
  const secondsLeft = state.voteDeadline
    ? Math.max(0, Math.ceil((state.voteDeadline - Date.now()) / 1000))
    : 0;

  return (
    <div className="results-v2-page">
      {youWon && <Fireworks />}
      {state.voteDeadline != null && (
        <div className={`vote-timer-pill ${secondsLeft <= 5 ? "is-urgent" : ""}`} aria-live="polite">
          <span className="vote-timer-num">{secondsLeft}</span>
          <span className="vote-timer-unit">sec</span>
        </div>
      )}
      <header className="results-v2-top">
        <span /> {/* spacer so the counter stays centered */}
        <div className="results-v2-counter">
          <span aria-hidden>👥</span> {state.players.length} players
        </div>
        <span /> {/* spacer */}
      </header>

      {/* Full-bleed 3D podium — player names float above each character's
          head inside the scene (no DOM name cards). */}
      <div className={`results-v2-podium ${step >= 1 ? "in" : ""}`}>
        {topThree.length > 0 && (
          <div className="results-v2-podium-stage">
            <BattlePodiumScene
              players={topThree.map((p) => ({
                id: p.id,
                displayName: p.displayName,
                avatarConfig: p.avatarConfig,
              }))}
              finale={finaleLevel}
            />
          </div>
        )}
      </div>

      {/* Ranks 4+ */}
      {restRanked.length > 0 && (
        <div className={`results-v2-rest ${step >= 2 ? "in" : ""}`}>
          {restRanked.map((p, i) => {
            const rank = i + 4;
            const isMe = p.id === me.id;
            return (
              <div key={p.id} className={`results-v2-rest-row ${isMe ? "is-me" : ""}`}>
                <span className="results-v2-rest-rank">{rank}</span>
                <span className="results-v2-rest-avatar">
                  <AvatarPreview cfg={p.avatarConfig} />
                </span>
                <span className="results-v2-rest-name">{p.displayName}{isMe && " (you)"}</span>
                <span className="results-v2-rest-score">
                  <span aria-hidden>⭐</span> {computedScore(p).toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Title + action buttons pinned to the bottom of the screen. */}
      <div className={`results-v2-bottom ${step >= 1 ? "in" : ""}`}>
      <h1 className="results-v2-title">
        <span aria-hidden>🗳️</span> Vote the next level!
      </h1>
      {state.voteOptions && state.voteOptions.length > 0 ? (
        <div className={`vote-panel ${step >= 3 ? "in" : ""}`}>
          <div className="vote-grid">
            {state.voteOptions.map((opt) => (
              <VoteLevelCard
                key={opt}
                lessonId={opt}
                count={voteCount(opt)}
                total={state.players.length}
                mine={myVote === opt}
                onVote={() => client?.vote(opt)}
              />
            ))}
          </div>
          <p className="results-v2-foot">
            {myVote
              ? "Tap a different level to change your vote"
              : "Tap a level — most votes wins when the timer ends"}
          </p>
          {isHost && !embedded && (
            <button className="vote-lobby-link" onClick={() => client?.backToLobby()}>
              Back to Lobby
            </button>
          )}
        </div>
      ) : (
        <p className="results-v2-foot">Loading next round…</p>
      )}
      </div>
    </div>
  );
}
